import { useState } from 'react'
import { GetStaticProps } from 'next'
import { compressToEncodedURIComponent } from 'lz-string'

import Layout from '../../container/layout/Layout'
import MortgageFAQSlice from '../../container/mortgage/mortgageFAQSlice'
import MortgageHeroCTASlice from '../../container/mortgage/mortgageHeroCTASlice'
import MortgageTwoPathSlice from '../../container/mortgage/mortgageTwoPathSlice'
import NewCalculatorSlice from '../../container/newCalculator/newCalculator'
import AudurPrismicClient from '../../prismic/prismicClient'
import {
	AudurFooter,
	AudurHeader,
	AudurMortgagePage,
	CalculatorFaqSlice,
	CalculatorSlice,
	HeroWithCtaSlice,
	TwoPathsSlice,
} from '../../prismic/prismicTypes'
import { prismicConstants, SITE_ALERT_REVALIDATE_SECONDS } from '../../utils/Constants'
import { TranslatedStrings } from '../../utils/translations/TranslatedStrings'
import { getAudurFooter, getAudurHeader, getAudurSiteAlert } from '../../utils/Utils'
import { SiteAlert } from '../../utils/types/Types'
import { Route } from '../../utils/navigation/Routes'
import { LocalCalculationValues } from '../../container/newCalculator/hooks/useCalculatorLogic'

import styles from './index.module.scss'

type ICalculatorProps = {
	footer: AudurFooter
	header: AudurHeader
	siteAlert: SiteAlert
	mortgagePage?: AudurMortgagePage
}

const Calculator = ({ footer, header, siteAlert, mortgagePage }: ICalculatorProps) => {
	const [loanCalculationCompressedValues, setLoanCalculationCompressedValues] = useState<string>()
	const { mortgagePage: mortgageStrings } = TranslatedStrings.is

	const mortgageData = mortgagePage?.body?.find((slice) => slice.type === 'calculator') as CalculatorSlice | undefined
	const twoPathSlice = mortgagePage?.body?.find((slice) => slice.type === 'two_paths_slice') as
		TwoPathsSlice | undefined
	const herowithCTAData = mortgagePage?.body?.find((slice) => slice.type === 'herowithcta') as
		HeroWithCtaSlice | undefined
	const calculatorFAQ = mortgagePage?.body?.find((slice) => slice.type === 'calculator_faq') as
		CalculatorFaqSlice | undefined

	const compressLoanValues = (loanValues: LocalCalculationValues) => {
		setLoanCalculationCompressedValues(compressToEncodedURIComponent(JSON.stringify(loanValues)))
	}

	return (
		<Layout
			title={mortgageStrings.heroDefaultTitle}
			metaTags={{
				title: mortgageStrings.heroDefaultTitle,
				description: mortgageStrings.heroDefaultDescription,
				imageUrl: mortgageStrings.imageUrl,
			}}
			footer={footer}
			header={header}
			siteAlert={siteAlert}
		>
			<div className={styles.mortgageWrap}>
				{herowithCTAData && (
					<MortgageHeroCTASlice
						{...herowithCTAData}
						displayIconButton
						hideImageMobile={false}
						firstButtonLink={
							loanCalculationCompressedValues
								? `${Route.ApplyForMortgage}?loanValues=${loanCalculationCompressedValues}&redirect=application-flow`
								: `${Route.ApplyForMortgage}?redirect=application-flow`
						}
					/>
				)}
				{twoPathSlice && <MortgageTwoPathSlice {...twoPathSlice?.primary} />}
				{mortgageData && (
					<NewCalculatorSlice
						prismicData={mortgageData?.primary}
						onLoanValuesChange={(values) => {
							compressLoanValues(values)
						}}
					/>
				)}
				{calculatorFAQ && <MortgageFAQSlice {...calculatorFAQ} />}
			</div>
		</Layout>
	)
}

export default Calculator

export const getStaticProps: GetStaticProps<ICalculatorProps> = async () => {
	const [footer, header, siteAlert] = await Promise.all([getAudurFooter(), getAudurHeader(), getAudurSiteAlert()])

	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const mortgagePage = await audurClient.getAllMortgagePages({
		variables: {
			lang: 'is',
		},
	})

	return {
		props: {
			footer,
			header,
			siteAlert,
			mortgagePage: mortgagePage?.edges?.[0]?.node || undefined,
		},
		revalidate: SITE_ALERT_REVALIDATE_SECONDS,
	}
}
